import type { CollectionSummaryJSONObject, TagJSONObject } from '@ikegps/core-api';
import { action, computed, observable, ObservableMap, reaction, makeObservable } from 'mobx';
import { Services } from '../services';
import { AppStore } from '../store';
import type { IkeState } from './ike.state';
import { PageState } from './page.state';
import { TagSortState } from './tag.sort.state';

export class TagSelectionState implements IkeState {
	pageState: PageState;

	/**
	 * Selected collections by tag.
	 *
	 * Tag fully selected:
	 *     tagId => true
	 *
	 * Tag partially selected:
	 *     tagId => collectionUUID => boolean
	 */
	private selectedByTag: ObservableMap<string, true | ObservableMap<string, boolean>> = observable.map<
		string,
		true | ObservableMap<string, boolean>
	>({}, { deep: false });

	@observable visibleTagListObjectIDs: string[] = [];
	@observable expandedCollectionsUUIDs: string[] = [];

	@observable private lastSelectedTagIndex: number = -1;
	@observable private lastSelectedCollectionIndex: number = -1;

	constructor(pageState: PageState) {
		makeObservable(this);

		this.pageState = pageState;

		reaction(
			(_) => this.pageState.isCtrlShiftPressed,
			(isCtrlShiftPressed) => {
				if (!isCtrlShiftPressed) {
					this.resetLastSelectedTagIndex();
					this.resetLastSelectedCollectionIndex();
				}
			}
		);
		this.reset();
	}

	reset(): void {
		this.selectedByTag.clear();
	}

	@action selectTag(tagId: string, selected = true) {
		const currentTagIndex = this.visibleTagListObjectIDs.indexOf(tagId);

		if (!this.pageState.isCtrlShiftPressed) {
			if (selected) {
				this.selectedByTag.set(tagId, true);
			} else {
				this.selectedByTag.delete(tagId);
			}
		} else {
			if (selected) {
				this.selectedByTag.set(tagId, true);
			}
			if (this.lastSelectedTagIndex !== -1 && this.lastSelectedTagIndex !== currentTagIndex) {
				this.findFromRange(this.visibleTagListObjectIDs, this.lastSelectedTagIndex, currentTagIndex, (tagIdInRange) => {
					this.selectedByTag.set(tagIdInRange, true);
				});
			}
		}
		if (selected) {
			this.lastSelectedTagIndex = currentTagIndex;
		}
		const tagSummary = AppStore.TagSummary.get(tagId);
		Services.Analytics.trackEvent(
			Services.Analytics.EventCategory.BigMap,
			'Select',
			`Tag:${selected ? 'On' : 'Off'}`,
			tagSummary.revisions.length
		);
	}

	@action selectAll(tags: TagJSONObject[], selected = true) {
		for (const tag of tags) {
			if (selected) {
				this.selectedByTag.set(tag.objectId, true);
			} else {
				this.selectedByTag.delete(tag.objectId);
			}
		}

		Services.Analytics.trackEvent(
			Services.Analytics.EventCategory.BigMap,
			'Select',
			`EveryTag:${selected ? 'On' : 'Off'}`,
			tags.length
		);
	}

	@action setExpandedCollectionsUUIDs(collectionUUIDs: string[]) {
		this.expandedCollectionsUUIDs = collectionUUIDs;
	}

	@action resetLastSelectedCollectionIndex() {
		this.lastSelectedCollectionIndex = -1;
	}

	@action resetLastSelectedTagIndex() {
		this.lastSelectedTagIndex = -1;
	}

	@action setVisibleTagListObjectIDs(tagListUUIDs: string[]) {
		this.visibleTagListObjectIDs = tagListUUIDs;
	}

	@action toggleCollection(tagId: string, collectionUUID: string) {
		const selected = !this.isCollectionSelected(tagId, collectionUUID);
		const selectedCollections = this.getExpandedTagCollections(tagId);

		this.selectedByTag.set(tagId, selectedCollections);

		if (selected) {
			selectedCollections.set(collectionUUID, true);
		} else {
			selectedCollections.delete(collectionUUID);
			if (selectedCollections.size === 0) {
				this.selectedByTag.delete(tagId);
			}
		}

		Services.Analytics.trackEvent(
			Services.Analytics.EventCategory.BigMap,
			'Select',
			`Collection:${selected ? 'On' : 'Off'}`
		);
	}

	@action selectCollection(tagId: string, collectionUUID: string, selected = true) {
		const selectedCollections = this.getExpandedTagCollections(tagId);
		const currentCollectionIndex = this.expandedCollectionsUUIDs.indexOf(collectionUUID);

		this.selectedByTag.set(tagId, selectedCollections);

		if (!this.pageState.isCtrlShiftPressed) {
			if (selected) {
				selectedCollections.set(collectionUUID, selected);
			} else {
				selectedCollections.delete(collectionUUID);
				if (selectedCollections.size === 0) {
					this.selectedByTag.delete(tagId);
				}
			}
		} else {
			if (selected) {
				selectedCollections.set(collectionUUID, selected);
			}
			if (this.lastSelectedCollectionIndex !== -1 && this.lastSelectedCollectionIndex !== currentCollectionIndex) {
				this.findFromRange(
					this.expandedCollectionsUUIDs,
					this.lastSelectedCollectionIndex,
					currentCollectionIndex,
					(collectionUUIDInRange) => {
						selectedCollections.set(collectionUUIDInRange, selected);
					}
				);
			}
		}
		if (selected) {
			this.lastSelectedCollectionIndex = currentCollectionIndex;
		}
		Services.Analytics.trackEvent(
			Services.Analytics.EventCategory.BigMap,
			'Select',
			`Collection:${selected ? 'On' : 'Off'}`
		);
	}

	isTagSelected(tagId: string): boolean {
		return this.selectedByTag.has(tagId);
	}

	isCollectionSelected(tagId: string, collectionUUID: string): boolean {
		const selectedCollections = this.selectedByTag.get(tagId);
		if (selectedCollections == null) {
			return false;
		}

		return selectedCollections === true || selectedCollections.has(collectionUUID);
	}

	collectionsSelectedSize(tagId: string): number {
		const selectedCollections = this.selectedByTag.get(tagId);
		if (selectedCollections == null) {
			return 0;
		}

		if (selectedCollections === true) {
			const tagSummary = AppStore.TagSummary.get(tagId);
			return tagSummary.revisions.length;
		}

		return selectedCollections.size;
	}

	get tagSize(): number {
		return this.selectedByTag.size;
	}

	@computed get collectionSize(): number {
		let size = 0;

		for (const tagId of Array.from(this.selectedByTag.keys())) {
			const selectedCollections = this.selectedByTag.get(tagId);
			if (selectedCollections === true) {
				const tagSummary = AppStore.TagSummary.get(tagId);
				size += tagSummary.revisions.length;
			} else {
				size += selectedCollections.size;
			}
		}

		return size;
	}

	@computed get collections(): CollectionSummaryJSONObject[] {
		const collectionSummaries: CollectionSummaryJSONObject[] = [];

		for (const tagId of this.tags) {
			for (const collectionUUID of this.getCollectionUUIDs(tagId)) {
				const collectionSummary = AppStore.CollectionSummary.getFetchableByUuid(collectionUUID).value;
				if (collectionSummary != null) {
					collectionSummaries.push(collectionSummary);
				}
			}
		}

		return TagSortState.sortSummaries(collectionSummaries);
	}

	@computed get ready(): boolean {
		let ready = true;

		// Attempt to load all tags and collection summaries
		const tagIds = Array.from(this.selectedByTag.keys());
		for (const tagId of tagIds) {
			ready = AppStore.Tag.get(tagId) != null && ready;
			for (const collectionUUID of this.getCollectionUUIDs(tagId)) {
				ready = AppStore.CollectionSummary.getFetchableByUuid(collectionUUID).value != null && ready;
			}
		}

		return ready;
	}

	@computed get tags(): string[] {
		const tags = Array.from(this.selectedByTag.keys()).map((tagId) => AppStore.Tag.get(tagId));

		return TagSortState.sortTags(tags).map((tag) => tag.objectId);
	}

	private getExpandedTagCollections(tagId: string): ObservableMap<string, boolean> {
		const expandedCollections = observable.map<string, boolean>();

		const selectedCollections = this.selectedByTag.get(tagId);
		if (selectedCollections == null) {
			return expandedCollections;
		}

		if (selectedCollections === true) {
			const tagSummary = AppStore.TagSummary.get(tagId);
			for (const revision of tagSummary.revisions) {
				const collectionSummary = AppStore.CollectionSummary.get(revision);
				if (collectionSummary != null) {
					expandedCollections.set(collectionSummary.uuid, true);
				}
			}

			return expandedCollections;
		}

		return selectedCollections;
	}

	private getCollectionUUIDs(tagId: string): string[] {
		const selectedCollections = this.selectedByTag.get(tagId);
		if (selectedCollections === null) {
			return [];
		}

		if (selectedCollections === true) {
			return Array.from(this.getExpandedTagCollections(tagId).keys());
		}

		return Array.from(selectedCollections.keys());
	}

	private findFromRange(
		items: string[],
		startIndex: number,
		endIndex: number,
		callback: (currentItem: string) => void
	) {
		const start = Math.min(startIndex, endIndex);
		const end = Math.max(startIndex, endIndex);
		for (let i = start; i <= end; i++) {
			callback(items[i]);
		}
	}
}
